import { useEffect, useState } from 'react';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';
import { formatCurrency } from '~/utils/paymentUtils';
import { centsToDollars, dollarsToCents } from './moneyInput';
import { useUpdateFamilyEnrollment } from './useUpdateFamilyEnrollment';

type FeeKind = 'REGULAR' | 'AMOUNT' | 'NONE';

interface EnrollmentFeeSettingProps {
  familyEnrollmentId: string;
  /** What the family year pays per child now. */
  fee: { kind: string; amountCents: number };
  /** The year's regular fee per child. */
  regularCents: number;
  onSaved?: () => void;
}

// The office sets ahead what this family pays per child for the year: the
// year's fee, another amount, or none. Saved through UpdateFamilyEnrollment;
// the server decides, and a refusal stays on screen.
export function EnrollmentFeeSetting({ familyEnrollmentId, fee, regularCents, onSaved }: EnrollmentFeeSettingProps) {
  const [kind, setKind] = useState<FeeKind>(fee.kind as FeeKind);
  const [amount, setAmount] = useState(centsToDollars(fee.kind === 'AMOUNT' ? fee.amountCents : regularCents));
  const [errors, setErrors] = useState<string[]>([]);
  const [update, { loading }] = useUpdateFamilyEnrollment();

  useEffect(() => {
    setKind(fee.kind as FeeKind);
    if (fee.kind === 'AMOUNT') setAmount(centsToDollars(fee.amountCents));
  }, [fee.kind, fee.amountCents]);

  const handleSave = async () => {
    let enrollmentFee: { kind: FeeKind; amountCents?: number } = { kind };
    if (kind === 'AMOUNT') {
      const cents = dollarsToCents(amount);
      if (cents === null) {
        setErrors(['Enter the fee per child in dollars']);
        return;
      }
      enrollmentFee = { kind, amountCents: cents };
    }
    try {
      const result = await update({ variables: { input: { familyEnrollmentId, enrollmentFee } } });
      const userErrors = result.data?.updateFamilyEnrollment?.userErrors ?? [];
      if (userErrors.length > 0) {
        setErrors(userErrors.map((e) => e.message));
        return;
      }
      setErrors([]);
      onSaved?.();
    } catch (error) {
      setErrors([error instanceof Error ? error.message : 'The fee was not saved. Please try again.']);
    }
  };

  const changed = kind !== fee.kind || (kind === 'AMOUNT' && dollarsToCents(amount) !== fee.amountCents);

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold">Enrollment fee</h4>
      <RadioGroup value={kind} onValueChange={(value) => setKind(value as FeeKind)}>
        <div className="flex items-center gap-2">
          <RadioGroupItem value="REGULAR" id="fee-regular" />
          <Label htmlFor="fee-regular">{`Regular (${formatCurrency(regularCents)} per child)`}</Label>
        </div>
        <div className="flex items-center gap-2">
          <RadioGroupItem value="AMOUNT" id="fee-amount" />
          <Label htmlFor="fee-amount">Another amount</Label>
        </div>
        <div className="flex items-center gap-2">
          <RadioGroupItem value="NONE" id="fee-none" />
          <Label htmlFor="fee-none">None</Label>
        </div>
      </RadioGroup>
      {kind === 'AMOUNT' && (
        <div className="max-w-[12rem] space-y-1">
          <Label htmlFor="fee-per-child">Fee per child</Label>
          <Input id="fee-per-child" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </div>
      )}
      {errors.length > 0 && (
        <div role="alert" className="space-y-1 text-sm text-destructive">
          {errors.map((message) => (
            <p key={message}>{message}</p>
          ))}
        </div>
      )}
      <Button size="sm" onClick={handleSave} disabled={loading || !changed}>
        Save fee
      </Button>
    </div>
  );
}

export default EnrollmentFeeSetting;
